// ChipToggle: an optional part of a form, switched in or out ("Multi-day", "Rain date",
// "Audience location"). A machined key with a hairline edge, not a pill: Chip is the filter
// pill, this is an option that usually reveals fields under it. Off it is hollow; on it fills
// with the gold tint, the edge turns gold and a check grows in, so the state is a shape as
// well as a colour. docs/operations/design-standard.md, "Controls".

import React, { useState } from 'react';
import type { LabelProps } from './Checkbox';
import { IconCheck } from './icons';
import { CONTROL_MOTION, PEER_FOCUS, PRESS, STATE, TOUCH } from './motion';

export interface ChipToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** A small drawing before the label, from ui/icons. */
  icon?: React.ReactNode;
  /** `sm` sits beside a section heading; `md` sits in the form body. */
  size?: 'sm' | 'md';
  children: React.ReactNode;
  /** On the wrapping label: a `title` hint, or an `onClick` that stops a click reaching a clickable row. Extra props on the control land on the hidden input, where neither works. */
  labelProps?: LabelProps;
}

const SIZE = { sm: 'h-6 gap-1 px-2.5 text-xs', md: 'h-7 gap-1.5 px-3 text-[13px]' };
/** A collapsed check still takes a gap after it; this hands the gap back. */
const COLLAPSED = { sm: '-mr-1', md: '-mr-1.5' };

const FACE = {
  on: 'bg-primary/15 text-primary-ink shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-primary,#ffcc33)_45%,transparent)]',
  off: 'bg-transparent text-on-surface-variant shadow-[inset_0_0_0_1px_var(--outline-variant)] hover:text-on-surface',
  disabled: 'bg-surface-container text-on-surface-disabled',
};

export const ChipToggle = React.forwardRef<HTMLInputElement, ChipToggleProps>(
  ({ icon, size = 'md', className = '', labelProps, children, checked, defaultChecked, onChange, disabled = false, ...rest }, ref) => {
    const [own, setOwn] = useState(!!defaultChecked);
    const on = checked !== undefined ? !!checked : own;
    return (
      <label {...labelProps} className={`relative inline-flex min-w-0 max-w-full flex-none ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}>
        <input
          ref={ref}
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          onChange={(e) => {
            if (checked === undefined) setOwn(e.target.checked);
            onChange?.(e);
          }}
          {...rest}
        />
        <span
          className={`inline-flex min-w-0 max-w-full select-none items-center rounded-button font-medium ${SIZE[size]} ${
            disabled ? FACE.disabled : `${on ? FACE.on : FACE.off} ${STATE} ${PRESS}`
          } ${CONTROL_MOTION} ${PEER_FOCUS} ${TOUCH}`}
        >
          {icon ? <span aria-hidden="true" className="-ml-0.5 inline-flex flex-none">{icon}</span> : null}
          <span
            aria-hidden="true"
            className={`inline-flex flex-none overflow-hidden transition-[width,margin,opacity] duration-standard ease-emphasized motion-reduce:transition-[opacity] ${
              on ? 'mr-0 w-3.5 opacity-100' : `${COLLAPSED[size]} w-0 opacity-0`
            }`}
          >
            <IconCheck className="h-3.5 w-3.5 flex-none" />
          </span>
          <span className="truncate">{children}</span>
        </span>
      </label>
    );
  },
);
ChipToggle.displayName = 'ChipToggle';

export default ChipToggle;
